import { Listbox, Portal } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/solid";
import { Placement } from "@popperjs/core";
import {
  HTMLAttributes,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { usePopper } from "react-popper";

import Button, {
  ButtonInSelectProps,
  ButtonSize,
  getLeftNegativeClassName,
} from "@components/button/button";
import { classNames } from "@helpers/css";

export type SelectOption<T = unknown> = {
  value: T;
  label: string | ReactNode | ((opts: { hovered: boolean }) => ReactNode);
  description?: string;
  disabled?: boolean;
};

const SelectOptionsPopover = ({
  options,
  selectedOption,
  popoverMinWidth,
  popoverMaxWidth,
  attributes,
  styles,
  setPopperElement,
  showSelectedIcon,
}: {
  options: SelectOption[];
  selectedOption?: SelectOption;
  popoverMinWidth: number;
  popoverMaxWidth: number;
  attributes: any;
  styles: any;
  setPopperElement: (element: HTMLElement | null) => void;
  showSelectedIcon: boolean;
}) => {
  return (
    <Listbox.Options
      ref={setPopperElement}
      style={{
        ...styles.popper,
        minWidth: `${popoverMinWidth}px`,
        maxWidth: `${popoverMaxWidth}px`,
      }}
      className={classNames(
        "z-dropdown bg-white shadow-lg rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none",
        `max-h-84`
      )}
      {...attributes.popper}
      aria-label="Dropdown options"
    >
      {options.map((option) => (
        <Listbox.Option
          key={`key-${option.value}`}
          className={({ active }) =>
            classNames(
              "w-auto",
              active ? "text-white bg-blue-600" : "text-gray-900",
              "cursor-default select-none relative py-2 px-3",
              showSelectedIcon &&
                selectedOption?.value === option.value &&
                "pr-9"
            )
          }
          value={option}
          disabled={option.disabled}
        >
          {({ selected, active, disabled }) => (
            <div
              className={classNames(disabled && "opacity-25 cusor-disabled")}
            >
              <div>
                <span
                  className={classNames(
                    selected ? "font-semibold" : "font-normal",
                    "block truncate"
                  )}
                >
                  {typeof option.label === "function"
                    ? option.label({ hovered: active })
                    : option.label}
                </span>

                {showSelectedIcon && selected ? (
                  <span
                    className={classNames(
                      active ? "text-white" : "text-blue-600",
                      "absolute inset-y-0 right-0 flex items-center pr-2"
                    )}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </span>
                ) : null}
              </div>
              {option.description && (
                <div
                  className={classNames(
                    "text-2xs tracking-tight",
                    active ? "text-white/70" : "text-gray-500"
                  )}
                >
                  {option.description}
                </div>
              )}
            </div>
          )}
        </Listbox.Option>
      ))}
    </Listbox.Options>
  );
};

type DeprecatedSelectProps<T> = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange" | "children"
> & {
  value: T | null | undefined;
  options: SelectOption<T>[];
  showSelectedIcon?: boolean;
  placement?: Placement;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  buttonProps?: ButtonInSelectProps;
  onChange: (opt: SelectOption<T>) => void;
};

const defaultButtonProps = {
  size: ButtonSize.large,
};

/**
 * @deprecated Use Select component instead
 */
const DeprecatedSelect = <T,>({
  value,
  options,
  showSelectedIcon = true,
  placement = "bottom-start",
  disabled = false,
  className,
  containerClassName = "",
  buttonProps = defaultButtonProps,
  onChange,
  ...props
}: DeprecatedSelectProps<T>) => {
  const selected = options.find((option) => option.value === value);
  if (!selected) {
    throw new Error(
      "Selected option not found. If you need a button to open a menu, use <Menu /> component with button={<Button />}"
    );
  }

  const handleChange = useCallback(
    (option: SelectOption<T>) => {
      onChange(option);
    },
    [onChange]
  );

  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  );
  const modifiers = useMemo(
    () => [
      {
        name: "offset",
        options: { offset: [0, 5] },
      },
    ],
    []
  );
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers,
  });

  const inputWidth = referenceElement?.getBoundingClientRect().width || 0;
  const popoverMinWidth = Math.max(inputWidth, 150);
  const popoverMaxWidth = Math.max(popoverMinWidth, 640);

  const mergedButtonProps = {
    ...defaultButtonProps,
    ...buttonProps,
  };
  return (
    <div
      className={classNames(
        containerClassName,
        "relative",
        mergedButtonProps.leftNegativeMargin &&
          getLeftNegativeClassName(mergedButtonProps.size)
      )}
      {...props}
    >
      <Listbox value={selected} onChange={handleChange} disabled={disabled}>
        {({ disabled }) => (
          <div className="relative">
            <Listbox.Button
              as={Button}
              className={classNames("flex-1 w-full text-left", className)}
              {...mergedButtonProps}
              leftNegativeMargin={false}
              showDownChevronIcon={!disabled}
              ref={setReferenceElement}
            >
              <div className="flex-1 block truncate flex-nowrap">
                {typeof selected.label === "function"
                  ? selected.label({ hovered: false })
                  : selected.label}
              </div>
            </Listbox.Button>
            <Portal>
              <SelectOptionsPopover
                options={options}
                selectedOption={selected}
                popoverMinWidth={popoverMinWidth}
                popoverMaxWidth={popoverMaxWidth}
                attributes={attributes}
                styles={styles}
                setPopperElement={setPopperElement}
                showSelectedIcon={showSelectedIcon}
              />
            </Portal>
          </div>
        )}
      </Listbox>
    </div>
  );
};

export const Select = <T,>({
  value,
  options,
  showSelectedIcon = true,
  onChange,
  placement = "bottom-start",
  disabled = false,
  className = "",
  containerClassName = "",
  buttonProps = defaultButtonProps,
  placeholder = "Select...",
  prefix,
  ...htmlDivProps
}: {
  value?: T;
  options: SelectOption<T>[];
  showSelectedIcon?: boolean;
  disabled?: boolean;
  placeholder?: string;
  placement?: Placement;
  className?: string;
  containerClassName?: string;
  buttonProps?: ButtonInSelectProps;
  onChange: (option: SelectOption<T>) => void;
  prefix?: string | JSX.Element;
} & Pick<HTMLAttributes<HTMLDivElement>, "aria-label">) => {
  const selected = options.find((option) => option.value === value);

  const handleChange = (option: SelectOption<T>) => {
    onChange(option);
  };

  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  );
  const modifiers = useMemo(
    () => [
      {
        name: "offset",
        options: { offset: [0, 5] },
      },
    ],
    []
  );
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers,
  });

  const inputWidth = referenceElement?.getBoundingClientRect().width || 0;
  // popover width is a minium of 150px
  const popoverMinWidth = Math.max(inputWidth, 150);
  // popover width is a maximum of 640px or the width of the input
  const popoverMaxWidth = Math.max(popoverMinWidth, 640);

  const mergedButtonProps = {
    ...defaultButtonProps,
    ...buttonProps,
  };
  return (
    <Listbox onChange={handleChange} disabled={disabled}>
      {({ disabled }) => (
        <div className={classNames(`flex min-w-0`, containerClassName)}>
          <Listbox.Button
            className={classNames("flex-1 w-full text-left", className)}
            {...mergedButtonProps}
            as={Button}
            showDownChevronIcon={!disabled}
            ref={setReferenceElement}
            {...htmlDivProps}
          >
            {selected ? (
              <span className="min-w-0 flex-1 block flex-nowrap flex items-center">
                {prefix && (
                  <span className="text-gray-500 font-normal mr-2 shrink-0">
                    {prefix}:
                  </span>
                )}
                <span className="truncate">
                  {typeof selected.label === "function"
                    ? selected.label({ hovered: false })
                    : selected.label}
                </span>
              </span>
            ) : (
              <span className="min-w-0 flex-1 block flex-nowrap flex items-center text-gray-400">
                {placeholder}
              </span>
            )}
          </Listbox.Button>

          <Portal>
            <SelectOptionsPopover
              attributes={attributes}
              styles={styles}
              setPopperElement={setPopperElement}
              options={options}
              selectedOption={selected}
              popoverMinWidth={popoverMinWidth}
              popoverMaxWidth={popoverMaxWidth}
              showSelectedIcon={showSelectedIcon}
            />
          </Portal>
        </div>
      )}
    </Listbox>
  );
};

export default DeprecatedSelect;
